import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AdvisorAdjustment } from 'app/models/advisor-adjustment';
import { AdvisorTypeListModel } from 'app/models/advisor-type-list.model';
import { saveAs as _importedSaveAs } from 'file-saver';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../environments/environment';
import { AdvisorPayout } from '../models/advisor-payout';
import { Advisor, AdvisorListItem, AdvisorResponse } from '../models/advisor.model';
import { FileUpload } from '../models/file-upload';
import { NameId } from '../models/nameId';

const URL = environment.apiBaseUrl;

@Injectable()
export class AdvisorsService {
  constructor(private http: HttpClient) {}

  public paymentMethods(): any {
    return [{ id: 0, text: 'Check' }, { id: 1, text: 'ACH' }];
  }

  public advisors(
    startRow: number,
    endRow: number,
    searchText: string,
    sortCol: string,
    sortDesc: boolean,
    showInactive: boolean
  ): Observable<AdvisorResponse> {
    const url =
      `${URL}advisors?startRow=${startRow}&endRow=${endRow}&searchString=${searchText}` +
      `&sortColumn=${sortCol}&sortDescending=${sortDesc}&showInactive=${showInactive}`;

    return this.http.get<AdvisorResponse>(url).map((x: any) => {
      const res = new AdvisorResponse();
      res.advisors = AdvisorListItem.fromJsonArray(x.data);
      res.total = x.total;
      return res;
    });
  }

  public advisorTypes() {
    return this.http.get<AdvisorTypeListModel[]>(`${URL}advisorTypes`);
  }

  public addAdvisor(advisor: Advisor) {
    return this.http.post(`${URL}advisors`, advisor);
  }

  public getAdvisor(id: number): Observable<Advisor> {
    return this.http.get<Advisor>(`${URL}advisors/${id}`);
  }

  public getAllSimple(): Observable<NameId[]> {
    return this.http.get<NameId[]>(`${URL}advisors/allSimple`);
  }

  public typeahead(typeahead: string): Observable<NameId[]> {
    return this.http.get<NameId[]>(`${URL}advisors/typeahead/${typeahead}`);
  }

  public updateAdvisor(advisor: Advisor) {
    return this.http.put(`${URL}advisors`, advisor);
  }

  public deactivate(advisorId: number) {
    return this.http.post(`${URL}advisors/deactivate/${advisorId}`, null);
  }

  public activate(advisorId: number) {
    return this.http.post(`${URL}advisors/activate/${advisorId}`, null);
  }

  public importFile(fileUpload: FileUpload) {
    return this.http.post(`${URL}advisors/import`, fileUpload);
  }

  public getPayout(advisorId: number, quarterId: number) {
    if (!advisorId) {
      advisorId = -1;
    }
    if (!quarterId) {
      quarterId = -1;
    }

    const url = `${URL}advisors/advisorPayouts/${advisorId}/${quarterId}`;
    return this.http.get<AdvisorPayout>(url);
  }

  public getTotalPayout(advisorId: number, quarterId: number): Observable<number> {
    if (!advisorId) {
      advisorId = -1;
    }
    if (!quarterId) {
      quarterId = -1;
    }

    const url = `${URL}advisors/advisorTotalPayouts/${advisorId}/${quarterId}`;
    return this.http.get<number>(url);
  }

  public getDeductionsForAdvisor(advisorId: number, quarterId: number): Observable<AdvisorAdjustment[]> {
    const url = `${URL}advisorAdjustments/adjustmentDeductions/${advisorId}/${quarterId}`;
    return this.http.get<AdvisorAdjustment[]>(url);
  }

  public updateAdvisorAdjustments(advisorId, quarterId, advisorAdjustmentVm: AdvisorAdjustment[]) {
    return this.http.post<AdvisorAdjustment[]>(
      `${URL}advisorAdjustments/updateAdvisorAdjustments/${advisorId}/${quarterId}`,
      advisorAdjustmentVm
    );
  }

  public downloadPayout(advisorId: number, quarterId: number, quarterName: string, advisorName: string) {
    return this.http
      .get(`${URL}advisors/payout/${advisorId}/${quarterId}`, {
        responseType: 'blob'
      })
      .subscribe((res: any) => _importedSaveAs(res, `${quarterName} - ${advisorName} - Advisor Payout.xlsx`));
  }

  public downloadPayoutTotals(quarterId: number, quarterName: string, finishedFunc: any) {
    return this.http.get(`${URL}advisors/payouttotals/${quarterId}`, { responseType: 'blob' }).subscribe((res: any) => {
      const filename = `${quarterName} - Advisor PayoutTotals.xlsx`;
      _importedSaveAs(res, filename);
      if (finishedFunc) {
        finishedFunc();
      }
    });
  }
}
